import classNames from 'classnames';
import type { ReactNode } from 'react';

import styles from './Stepper.module.css';

export type StepperStepStatus = 'complete' | 'active' | 'incomplete';

interface StepperStep {
  /** Step label (e.g. "Upload File"). */
  label: ReactNode;
  /** Optional secondary line under the label (e.g. "CSV or Excel"). */
  meta?: ReactNode;
  /**
   * Optional block of supporting content under the label/meta (e.g. the list
   * of selected datasets). Rendered in a muted 12px slot; span-level content
   * only (the slot is an inline element).
   */
  details?: ReactNode;
  /**
   * Per-step clickability. Only meaningful when `onStepClick` is provided;
   * defaults to true in that case. Non-clickable steps render as static rows.
   */
  clickable?: boolean;
  /** Optional test id applied to the step's numbered box. */
  'data-testid'?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Index of the current step. */
  activeIndex: number;
  /**
   * Indices rendered as completed. Defaults to every index below
   * `activeIndex` (a strictly linear flow). The active index always renders
   * as active, even if listed here.
   */
  completedIndices?: number[];
  /** When provided, clickable steps render as buttons and call back on click. */
  onStepClick?: (index: number) => void;
  className?: string;
  'data-testid'?: string;
}

const getStepStatus = (
  index: number,
  activeIndex: number,
  completedIndices: number[] | undefined
): StepperStepStatus => {
  if (index === activeIndex) return 'active';
  const isComplete = completedIndices
    ? completedIndices.includes(index)
    : index < activeIndex;
  return isComplete ? 'complete' : 'incomplete';
};

/**
 * Vertical numbered-box stepper (Figma v1.1 deliverable rails): each step is a
 * 32px numbered box with a label (+ optional meta line and details block),
 * joined by a connector rule that spans the step's full height — dashed while
 * the step is pending, solid once it completes. Monochrome: active = full-ink
 * border, complete = inverse ink fill, incomplete = faint ink fill +
 * secondary number.
 *
 * Presentation-only — status derives from `activeIndex` / `completedIndices`,
 * navigation stays with the caller via `onStepClick`.
 */
export const Stepper = ({
  steps,
  activeIndex,
  completedIndices,
  onStepClick,
  className,
  'data-testid': dataTestId,
}: StepperProps) => (
  <ol
    className={classNames(styles.stepper, className)}
    data-testid={dataTestId}
  >
    {steps.map((step, index) => {
      const status = getStepStatus(index, activeIndex, completedIndices);
      const isClickable = !!onStepClick && step.clickable !== false;
      const ariaCurrent = status === 'active' ? ('step' as const) : undefined;
      const key = typeof step.label === 'string' ? step.label : index;
      const content = (
        <>
          <span className={styles.indicator}>
            <span
              className={styles.box}
              data-status={status}
              data-testid={step['data-testid']}
            >
              {index + 1}
            </span>
            {index < steps.length - 1 && (
              <span className={styles.connector} aria-hidden />
            )}
          </span>
          <span className={styles.text}>
            <span className={styles.label}>{step.label}</span>
            {step.meta != null && step.meta !== '' && (
              <span className={styles.meta}>{step.meta}</span>
            )}
            {step.details != null && step.details !== '' && (
              <span className={styles.details}>{step.details}</span>
            )}
          </span>
        </>
      );
      return (
        <li className={styles.step} data-status={status} key={key}>
          {isClickable ? (
            <button
              type="button"
              className={styles.stepButton}
              aria-current={ariaCurrent}
              onClick={() => onStepClick(index)}
            >
              {content}
            </button>
          ) : (
            <span className={styles.stepStatic} aria-current={ariaCurrent}>
              {content}
            </span>
          )}
        </li>
      );
    })}
  </ol>
);
